/**
 * Utility function to validate input fields and update validation state.
 *
 * @param {Object} fields - An object where keys are field names and values are either:
 *                          - the field value (for required check), or
 *                          - a function that returns a boolean indicating validity.
 * @param {Function} setValidator - State updater function to store validation results.
 * @returns {boolean} - Returns true if all fields are valid, false otherwise.
 */
export default function validateFields(fields, setValidator) {
    const validationResult = {};
    for (const [key, rule] of Object.entries(fields)) {
        let isInvalid;

        if (typeof rule === 'function') {
            // If rule is a function, execute it to get the validation result.
            // If the result is true (valid), mark isInvalid as false.
            isInvalid = !rule();
        } else {
            // If rule is a value, perform basic required check.
            // Consider invalid if null, undefined, empty string, or string with only whitespace.
            isInvalid = rule === null || rule === undefined || (typeof rule === 'string' && rule.trim() === '');
        }

        // Store the validation result for the field.
        validationResult[key] = isInvalid;
    }

    // Update validation state with the result for each field.
    setValidator((prev) => ({
        ...prev,
        ...validationResult,
    }));

    // Return true only if all fields are valid.
    return !Object.values(validationResult).includes(true);
}
